import React, { useMemo } from 'react';

import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import { Button, Chip, Stack, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import { Voucher, VoucherToggle } from '~/services/PromoService';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import CopyableField from '../Common/CopyableField';

import VoucherToggleStack from './VoucherToggleStack';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: true,
  disableColumnMenu: true,
  flex: 0.5,
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ONE_WEEK_MS = 7 * ONE_DAY_MS;
const THIRTY_DAYS_MS = 30 * ONE_DAY_MS;

function getRecentDateChipColor(date: string): ChipProps['color'] | null {
  const ageMs = Date.now() - dayjs(date).valueOf();

  if (!Number.isFinite(ageMs) || ageMs < ONE_DAY_MS) {
    return 'success';
  }
  if (ageMs < ONE_WEEK_MS) {
    return 'warning';
  }
  if (ageMs > THIRTY_DAYS_MS) {
    return null;
  }
  return 'default';
}

function getExpiryChipColor(date: string): ChipProps['color'] | null {
  const expiry = dayjs(date);
  if (!expiry.isValid()) {
    return null;
  }
  if (!expiry.isAfter(dayjs())) {
    return 'error';
  }
  return expiry.isBefore(dayjs().add(3, 'month')) ? 'warning' : null;
}

function DateWithRelativeTime({
  date,
  onSortClick,
  sortLabel,
}: {
  date?: string;
  onSortClick?: () => void;
  sortLabel: string;
}) {
  if (!date) {
    return '—';
  }

  const color = getRecentDateChipColor(date);
  return (
    <Stack spacing={0.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
      <DateFormatterDetailed date={date} />
      {color && (
        <Chip
          label={dayjs(date).fromNow()}
          color={color}
          size="small"
          variant="outlined"
          onClick={
            onSortClick
              ? (event) => {
                  event.stopPropagation();
                  onSortClick();
                }
              : undefined
          }
          aria-label={onSortClick ? `Sort by ${sortLabel}` : undefined}
        />
      )}
    </Stack>
  );
}

type Props = {
  auditUsersById: Map<string, App.UserSummary>;
  onVoucherToggleClick: (toggle: VoucherToggle) => void;
  onVoucherReportingTagClick: (tag: string) => void;
  onDateSortClick: (field: 'createdAt' | 'updatedAt') => void;
  selectedVoucherToggle?: VoucherToggle;
};

function userName(userId: string, auditUsersById: Map<string, App.UserSummary>): string {
  return auditUsersById.get(userId)?.full_name || userId || '—';
}

/** The shared, non-action voucher columns used by list-style voucher tables. */
export function useVoucherColumns({
  auditUsersById,
  onVoucherToggleClick,
  onVoucherReportingTagClick,
  onDateSortClick,
  selectedVoucherToggle,
}: Props) {
  return useMemo<Array<GridColDef<Voucher>>>(
    () => [
      {
        field: 'voucherId',
        headerName: 'ID',
        ...sharedOpts,
        renderCell: (params) => params.row.voucherId || '',
      },
      {
        field: 'voucherName',
        headerName: 'Voucher Name',
        ...sharedOpts,
        sortable: true,
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const voucherName = params.row.voucherName || '—';

          return (
            <CopyableField
              value={voucherName}
              tooltip="Copy voucher name"
              sx={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}
              label={
                <Typography component="span" title={voucherName} noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {voucherName}
                </Typography>
              }
            />
          );
        },
      },
      {
        field: 'voucherTitle',
        headerName: 'Title',
        ...sharedOpts,
        renderCell: (params) => params.row.voucherTitle || '',
      },
      {
        field: 'voucherToggles',
        headerName: 'Toggles',
        ...sharedOpts,
        flex: 1,
        renderCell: (params) => (
          <VoucherToggleStack
            toggles={params.row.voucherToggles}
            selectedToggle={selectedVoucherToggle}
            onToggleClick={onVoucherToggleClick}
          />
        ),
      },
      {
        field: 'voucherExpiry',
        headerName: 'Expiry',
        ...sharedOpts,
        renderCell: (params) => {
          const expiryDate = params.row.voucherUserExpiresAt;
          const expiryColor = expiryDate ? getExpiryChipColor(expiryDate) : null;

          return (
            <Stack spacing={0.25} alignItems="flex-start">
              {params.row.voucherUserExpiresInDays != null ? (
                <Typography variant="body2" color="text.secondary">
                  {`${params.row.voucherUserExpiresInDays} days`}
                </Typography>
              ) : expiryDate ? (
                <>
                  <DateFormatterDetailed date={expiryDate} highlightIfPast />
                  {expiryColor && (
                    <Chip label={dayjs(expiryDate).fromNow()} color={expiryColor} size="small" variant="outlined" />
                  )}
                </>
              ) : (
                '—'
              )}
            </Stack>
          );
        },
      },
      {
        field: 'caseId',
        headerName: 'Case ID',
        ...sharedOpts,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.row.caseId || ''}
            {params.row.reason && (
              <Typography variant="body2" color="text.secondary">
                {params.row.reason}
              </Typography>
            )}
          </Typography>
        ),
      },
      {
        field: 'parentVoucherId',
        headerName: 'Parent voucher',
        ...sharedOpts,
        valueGetter: (_value, row) => row.clonedFromId || '',
        renderCell: (params) => {
          const id = params.value as string;
          return id ? (
            <Button
              variant="text"
              size="small"
              component={Link}
              to={`/marketing/vouchers/${id}`}
              sx={{ p: 0, minWidth: 0 }}
            >
              {id}
            </Button>
          ) : (
            ''
          );
        },
      },
      { field: 'theme', headerName: 'Theme', ...sharedOpts, renderCell: (params) => params.row.theme || '' },
      {
        field: 'productType',
        headerName: 'Product Type',
        ...sharedOpts,
        renderCell: (params) => params.row.productType || '',
      },
      {
        field: 'createdBy',
        headerName: 'Created by',
        ...sharedOpts,
        renderCell: (params) => (
          <CopyableField
            value={params.row.createdBy}
            label={userName(params.row.createdBy, auditUsersById)}
            tooltip="Copy creator ID"
          />
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        ...sharedOpts,
        sortable: true,
        renderCell: (params) => (
          <DateWithRelativeTime
            date={params.row.createdAt}
            sortLabel="Created At"
            onSortClick={() => onDateSortClick('createdAt')}
          />
        ),
      },
      {
        field: 'updatedBy',
        headerName: 'Modified by',
        ...sharedOpts,
        renderCell: (params) => (
          <CopyableField
            value={params.row.updatedBy}
            label={userName(params.row.updatedBy, auditUsersById)}
            tooltip="Copy modifier ID"
          />
        ),
      },
      {
        field: 'updatedAt',
        headerName: 'Modified At',
        ...sharedOpts,
        sortable: true,
        renderCell: (params) => (
          <DateWithRelativeTime
            date={params.row.updatedAt}
            sortLabel="Modified At"
            onSortClick={() => onDateSortClick('updatedAt')}
          />
        ),
      },
      {
        field: 'voucherReportingTags',
        headerName: 'Voucher reporting tags',
        ...sharedOpts,
        flex: 1,
        minWidth: 220,
        renderCell: (params) => {
          const tags = params.row.voucherReportingTags ?? [];

          if (tags.length === 0) {
            return '';
          }

          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={() => onVoucherReportingTagClick(tag)}
                  aria-label={`Include reporting tag ${tag}`}
                />
              ))}
            </Stack>
          );
        },
      },
      {
        field: 'updatedBy',
        headerName: 'Modified by',
        ...sharedOpts,
        renderCell: (params) => (
          <CopyableField
            value={params.row.updatedBy}
            label={userName(params.row.updatedBy, auditUsersById)}
            tooltip="Copy modifier ID"
          />
        ),
      },
      {
        field: 'updatedAt',
        headerName: 'Modified At',
        ...sharedOpts,
        sortable: true,
        renderCell: (params) => (
          <DateWithRelativeTime
            date={params.row.updatedAt}
            sortLabel="Modified At"
            onSortClick={() => onDateSortClick('updatedAt')}
          />
        ),
      },
      {
        field: 'createdBy',
        headerName: 'Created by',
        ...sharedOpts,
        renderCell: (params) => (
          <CopyableField
            value={params.row.createdBy}
            label={userName(params.row.createdBy, auditUsersById)}
            tooltip="Copy creator ID"
          />
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        ...sharedOpts,
        sortable: true,
        renderCell: (params) => (
          <DateWithRelativeTime
            date={params.row.createdAt}
            sortLabel="Created At"
            onSortClick={() => onDateSortClick('createdAt')}
          />
        ),
      },
    ],
    [auditUsersById, onDateSortClick, onVoucherReportingTagClick, onVoucherToggleClick, selectedVoucherToggle],
  );
}
